// 국가별 현황 보드(홈) — distributor-desk Design §3, FR-5.
// 정제된 운영 콘솔 룩: KPI stat 카드 + 국가별 그룹 원장(ledger, 시그니처).
// 실적 수식은 flow 와 공유(saleCalc). GT 규칙: 확정 실적 행만 집계.
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { krw, saleCalc, usd } from "@/lib/margin/model";
import { saleToModel } from "@/flow/convert";
import { useDistributorData } from "./DistributorDeskLayout";
import {
  Badge,
  btnGhost,
  Card,
  cx,
  Disclaimer,
  EmptyState,
  IconBoard,
  IconPin,
  PageHeader,
  StatCard,
  StatusDot,
  tdCls,
  tdNum,
  thCls,
  type Tone,
} from "./ui";

const maxDate = (xs: (string | null | undefined)[]): string | null => {
  const v = xs.filter((x): x is string => Boolean(x)).sort();
  return v.length ? v[v.length - 1]! : null;
};
const dayOnly = (s: string | null): string => (s ? s.slice(0, 10) : "—");

const STATUS_TONE: Record<string, Tone> = { active: "success", dormant: "warn", terminated: "neutral" };
const STATUS_LABEL: Record<string, string> = { active: "활성", dormant: "휴면", terminated: "종료" };
const EXCL_TONE: Record<string, Tone> = { exclusive: "accent", non_exclusive: "neutral", unset: "neutral" };
const EXCL_LABEL: Record<string, string> = { exclusive: "독점", non_exclusive: "비독점", unset: "미지정" };

interface BoardRow {
  relId: string;
  countryId: string;
  countryLabel: string;
  partnerName: string;
  tier: string | null;
  status: string;
  exclusivity: string;
  activeContracts: number;
  totalContracts: number;
  lastDealAt: string | null;
  dealCount: number;
  openIssues: number;
  lastProvidedAt: string | null;
  lastAmendedAt: string | null;
  soldQty: number;
  revenueKrw: number;
}

export function DistributorHome(): JSX.Element {
  const d = useDistributorData();

  const rows = useMemo<BoardRow[]>(() => {
    const countryLabel = (id: string) => {
      const c = d.countries.find((x) => x.id === id);
      return c ? (c.name_ko ?? c.name_en) : id;
    };
    const partnerName = (id: string) => d.partners.find((p) => p.id === id)?.company_name ?? "(미상 총판)";

    return d.relationships.map((rel) => {
      const relContracts = d.contracts.filter((c) => c.distributor_relationship_id === rel.id);
      const contractIds = new Set(relContracts.map((c) => c.id));
      const relDeals = d.deals.filter((x) => x.contract_id != null && contractIds.has(x.contract_id));
      const openIssues = d.issues.filter(
        (i) =>
          (i.status === "open" || i.status === "in_progress") &&
          (i.partner_id === rel.partner_id || (i.contract_id != null && contractIds.has(i.contract_id))),
      ).length;
      const relSales = d.sales.filter((s) => contractIds.has(s.contract_id));
      return {
        relId: rel.id,
        countryId: rel.country_id,
        countryLabel: countryLabel(rel.country_id),
        partnerName: partnerName(rel.partner_id),
        tier: rel.relationship_tier,
        status: rel.status,
        exclusivity: rel.exclusivity,
        activeContracts: relContracts.filter((c) => c.status === "active").length,
        totalContracts: relContracts.length,
        lastDealAt: maxDate(relDeals.map((x) => x.created_at)),
        dealCount: relDeals.length,
        openIssues,
        lastProvidedAt: maxDate(d.provisions.filter((p) => p.partner_id === rel.partner_id).map((p) => p.provided_at)),
        lastAmendedAt: maxDate(d.amendments.filter((a) => contractIds.has(a.contract_id)).map((a) => a.amended_at)),
        soldQty: relSales.reduce((sum, r) => sum + (r.qty || 0), 0),
        revenueKrw: relSales.reduce((sum, r) => sum + saleCalc(saleToModel(r)).revenueKrw, 0),
      };
    });
  }, [d]);

  const groups = useMemo(() => {
    const byCountry = new Map<string, BoardRow[]>();
    for (const r of rows) {
      if (!byCountry.has(r.countryLabel)) byCountry.set(r.countryLabel, []);
      byCountry.get(r.countryLabel)!.push(r);
    }
    return [...byCountry.entries()]
      .map(([label, rs]) => ({
        label,
        rows: [...rs].sort((a, b) => b.revenueKrw - a.revenueKrw),
        revenueKrw: rs.reduce((s, x) => s + x.revenueKrw, 0),
        openIssues: rs.reduce((s, x) => s + x.openIssues, 0),
      }))
      .sort((a, b) => b.revenueKrw - a.revenueKrw);
  }, [rows]);

  const kpi = useMemo(() => {
    const activeRel = d.relationships.filter((r) => r.status === "active").length;
    const activeContracts = d.contracts.filter(
      (c) => c.status === "active" && c.distributor_relationship_id != null,
    ).length;
    const openIssues = d.issues.filter((i) => i.status === "open" || i.status === "in_progress").length;
    const cutoff = new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10);
    const recentProvisions = d.provisions.filter((p) => p.provided_at >= cutoff).length;
    return { activeRel, activeContracts, openIssues, recentProvisions };
  }, [d]);

  const HEAD = ["총판", "관계", "활성계약", "최근 발주", "이슈", "최근 자료", "최근 개정", "누적", "매출 KRW"];

  return (
    <div>
      <PageHeader
        eyebrow="Overview"
        title="국가별 총판 현황"
        subtitle="확정 실적 기준 집계 · 관세·현지세금 제외 · 계약 개정 이력은 달성률과 별개로 관리됩니다."
        actions={
          <Link to="/distributor/relationships" className={btnGhost}>
            총판 관계 관리
          </Link>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="활성 총판" value={kpi.activeRel} unit="곳" tone="accent" />
        <StatCard label="활성 계약" value={kpi.activeContracts} unit="건" />
        <StatCard
          label="미해결 이슈"
          value={kpi.openIssues}
          unit="건"
          tone={kpi.openIssues > 0 ? "warn" : "neutral"}
        />
        <StatCard label="최근 30일 자료공유" value={kpi.recentProvisions} unit="건" tone="info" />
      </div>

      {d.loading ? (
        <Card className="px-4 py-16">
          <div className="text-center text-sm text-slate-400">불러오는 중…</div>
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<IconBoard className="h-8 w-8" />}
          title="등록된 총판 관계가 없습니다"
          action={
            <Link to="/distributor/relationships" className={btnGhost}>
              총판 관계 등록하기
            </Link>
          }
        >
          총판(파트너)×국가 관계를 등록하고 계약을 연결하면, 국가별 현황이 이 보드에 취합됩니다.
        </EmptyState>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200/70">
                  {HEAD.map((h, i) => (
                    <th key={h} className={cx(thCls, i >= 2 && "text-right")}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.map((g) => (
                  <GroupBlock key={g.label} g={g} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Disclaimer>
        본 도구는 사전 정의된 항목만 취합·표시합니다. 계약 조건·달성률·이슈 판단의 최종 확인은 담당자 책임입니다.
      </Disclaimer>
    </div>
  );
}

function GroupBlock({
  g,
}: {
  g: { label: string; rows: BoardRow[]; revenueKrw: number; openIssues: number };
}): JSX.Element {
  return (
    <>
      <tr className="bg-slate-50/70">
        <td colSpan={9} className="px-3 py-2">
          <div className="flex items-center gap-2">
            <IconPin className="h-4 w-4 text-indigo-400" />
            <span className="text-[13px] font-semibold text-slate-800">{g.label}</span>
            <span className="text-[11px] text-slate-400">
              총판 {g.rows.length}
              {g.revenueKrw > 0 && <> · 매출 {krw(g.revenueKrw)}원</>}
            </span>
            {g.openIssues > 0 && (
              <Badge tone="warn" dot>
                이슈 {g.openIssues}
              </Badge>
            )}
          </div>
        </td>
      </tr>
      {g.rows.map((r) => (
        <tr key={r.relId} className="transition hover:bg-slate-50/60">
          <td className={tdCls}>
            <div className="flex items-center gap-2">
              <StatusDot tone={STATUS_TONE[r.status] ?? "neutral"} />
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-800">{r.partnerName}</div>
                {r.tier && <div className="text-[11px] text-slate-400">{r.tier}</div>}
              </div>
            </div>
          </td>
          <td className={tdCls}>
            <div className="flex flex-wrap items-center gap-1">
              <Badge tone={STATUS_TONE[r.status] ?? "neutral"}>{STATUS_LABEL[r.status] ?? r.status}</Badge>
              {r.exclusivity !== "unset" && (
                <Badge tone={EXCL_TONE[r.exclusivity] ?? "neutral"}>{EXCL_LABEL[r.exclusivity]}</Badge>
              )}
            </div>
          </td>
          <td className={tdNum}>
            {r.activeContracts}
            {r.totalContracts > r.activeContracts && (
              <span className="text-slate-300"> / {r.totalContracts}</span>
            )}
          </td>
          <td className={tdNum}>
            {r.lastDealAt ? (
              <span>
                {dayOnly(r.lastDealAt)}
                <span className="text-slate-300"> ({r.dealCount})</span>
              </span>
            ) : (
              <span className="text-slate-300">—</span>
            )}
          </td>
          <td className={tdNum}>
            {r.openIssues > 0 ? (
              <span className="font-semibold text-amber-600">{r.openIssues}</span>
            ) : (
              <span className="text-slate-300">—</span>
            )}
          </td>
          <td className={cx(tdNum, !r.lastProvidedAt && "text-slate-300")}>{dayOnly(r.lastProvidedAt)}</td>
          <td className={cx(tdNum, !r.lastAmendedAt && "text-slate-300")}>{dayOnly(r.lastAmendedAt)}</td>
          <td className={tdNum}>{r.soldQty ? usd(r.soldQty) : <span className="text-slate-300">—</span>}</td>
          <td className={cx(tdNum, "font-medium text-slate-800")}>
            {r.revenueKrw ? krw(r.revenueKrw) : <span className="font-normal text-slate-300">—</span>}
          </td>
        </tr>
      ))}
    </>
  );
}
